import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Switch,
  Alert,
  ActivityIndicator,
  FlatList,
  Platform,
  Dimensions
} from 'react-native';
import Icon from '@expo/vector-icons/MaterialIcons';
import { doc, updateDoc, deleteDoc, serverTimestamp } from '../services/cachedFirestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';

const defaultColors = {
  beeYellow: '#F4C430',
  honeyGold: '#D4AF37',
  darkYellow: '#B8860B',
  deepNavy: '#1A1B3E',
  charcoal: '#2C2C2C',
  cream: '#FFF9E6',
  white: '#FFFFFF',
  warmGray: '#6B6B6B',
  lightGray: '#F5F5F5',
  success: '#27AE60',
  danger: '#E74C3C',
  warning: '#F39C12'
};

interface TelegramChannel {
  id: string;
  username: string;
  name: string;
  imageUrl?: string;
  category: string;
  isActive: boolean;
  scrapingEnabled: boolean;
  totalJobsScraped: number;
  lastScraped: any | null;
  createdAt: any;
  updatedAt: any;
}

interface TelegramChannelsListProps {
  channels: TelegramChannel[];
  onChannelsUpdate: () => void;
  loading: boolean;
  colors?: any;
  isDarkMode?: boolean;
}

export default function TelegramChannelsList({ 
  channels, 
  onChannelsUpdate, 
  loading,
  colors = defaultColors,
  isDarkMode = false,
}: TelegramChannelsListProps) {
  const [updatingChannels, setUpdatingChannels] = useState<Set<string>>(new Set());
  const [dimensions, setDimensions] = useState(Dimensions.get('window'));

  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => setDimensions(window));
    return () => sub?.remove();
  }, []);

  const isMobile = dimensions.width < 768;

  const updateChannel = async (channelId: string, updates: Partial<TelegramChannel>) => {
    setUpdatingChannels(prev => new Set(prev).add(channelId));
    
    try {
      const channelRef = doc(db, 'telegramChannels', channelId);
      await updateDoc(channelRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      onChannelsUpdate();
    } catch (error) {
      console.error('Error updating channel:', error);
      Alert.alert('Error', 'Failed to update channel');
    } finally {
      setUpdatingChannels(prev => {
        const newSet = new Set(prev);
        newSet.delete(channelId);
        return newSet;
      });
    }
  };

  const deleteChannel = async (channel: TelegramChannel) => {
    const confirmDelete = () => {
      return new Promise<boolean>((resolve) => {
        if (Platform.OS === 'web') {
          resolve(window.confirm(`Are you sure you want to delete @${channel.username}? This action cannot be undone.`));
        } else {
          Alert.alert(
            'Delete Channel',
            `Are you sure you want to delete @${channel.username}? This action cannot be undone.`,
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Delete', style: 'destructive', onPress: () => resolve(true) }
            ]
          );
        }
      });
    };

    const confirmed = await confirmDelete();
    if (!confirmed) return;

    setUpdatingChannels(prev => new Set(prev).add(channel.id));
    
    try {
      // Delete channel image if it exists
      if (channel.imageUrl) {
        try {
          const imageRef = ref(storage, channel.imageUrl);
          await deleteObject(imageRef);
        } catch (imageError) {
          console.warn('Failed to delete channel image:', imageError);
        }
      }

      // Delete channel document
      const channelRef = doc(db, 'telegramChannels', channel.id);
      await deleteDoc(channelRef);
      
      onChannelsUpdate();
      
      if (Platform.OS === 'web') {
        alert('Channel deleted successfully');
      } else {
        Alert.alert('Success', 'Channel deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting channel:', error);
      
      if (Platform.OS === 'web') {
        alert('Failed to delete channel');
      } else {
        Alert.alert('Error', 'Failed to delete channel');
      }
    } finally {
      setUpdatingChannels(prev => {
        const newSet = new Set(prev);
        newSet.delete(channel.id);
        return newSet;
      });
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Never';
    try {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    } catch {
      return 'Never';
    }
  };

  // Pad array to fill rows of 4 columns (desktop only)
  const padArrayForGrid = (array: TelegramChannel[]) => {
    const paddedArray = [...array];
    const remainder = paddedArray.length % 4;
    if (remainder !== 0) {
      const placeholdersNeeded = 4 - remainder;
      for (let i = 0; i < placeholdersNeeded; i++) {
        paddedArray.push(null as any);
      }
    }
    return paddedArray;
  };

  // ── Mobile card (full-width horizontal layout) ─────────────────────────────
  const renderMobileCard = ({ item: channel }: { item: TelegramChannel }) => {
    const isUpdating = updatingChannels.has(channel.id);
    const bg = isDarkMode ? '#161F2E' : '#FFFFFF';
    const border = isDarkMode ? '#1E293B' : '#E2E8F0';
    const textPrimary = isDarkMode ? '#F1F5F9' : '#0F172A';
    const textSecondary = isDarkMode ? '#94A3B8' : '#64748B';

    return (
      <View style={{
        backgroundColor: bg,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: border,
        borderLeftWidth: 4,
        borderLeftColor: channel.isActive && channel.scrapingEnabled ? '#10B981' : '#64748B',
        marginHorizontal: 12,
        marginBottom: 10,
        overflow: 'hidden',
        ...Platform.select({
          web: { boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)' },
          default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 2 },
        }),
      }}>
        {/* Top row: avatar + info + delete */}
        <View style={{ flexDirection: 'row', alignItems: 'center', padding: 14, gap: 12 }}>
          {/* Avatar */}
          <View style={{
            width: 48, height: 48, borderRadius: 24,
            backgroundColor: isDarkMode ? '#1E293B' : '#F1F5F9',
            justifyContent: 'center', alignItems: 'center',
            overflow: 'hidden', flexShrink: 0,
          }}>
            {channel.imageUrl ? (
              <Image source={{ uri: channel.imageUrl }} style={{ width: 48, height: 48 }} />
            ) : (
              <Text style={{ fontSize: 22 }}>📱</Text>
            )}
          </View>

          {/* Name / username / category */}
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              numberOfLines={1}
              style={{ fontSize: 14, fontWeight: '700', color: textPrimary, marginBottom: 1 }}
            >
              @{channel.username}
            </Text>
            <Text
              numberOfLines={1}
              style={{ fontSize: 12, color: textSecondary, marginBottom: 2 }}
            >
              {channel.name}
            </Text>
            <View style={{
              alignSelf: 'flex-start',
              backgroundColor: isDarkMode ? '#1E293B' : '#F1F5F9',
              paddingHorizontal: 7, paddingVertical: 2,
              borderRadius: 6,
            }}>
              <Text style={{ fontSize: 10, fontWeight: '600', color: textSecondary, textTransform: 'capitalize' }}>
                {channel.category}
              </Text>
            </View>
          </View>

          {/* Delete button */}
          <TouchableOpacity
            onPress={() => deleteChannel(channel)}
            disabled={isUpdating}
            style={{
              width: 34, height: 34, borderRadius: 8,
              backgroundColor: '#EF444420',
              justifyContent: 'center', alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {isUpdating
              ? <ActivityIndicator size="small" color="#EF4444" />
              : <Icon name="delete" size={17} color="#EF4444" />
            }
          </TouchableOpacity>
        </View>

        {/* Stats row */}
        <View style={{
          flexDirection: 'row',
          paddingHorizontal: 14, paddingBottom: 12,
          gap: 8,
        }}>
          <View style={{
            flex: 1, backgroundColor: isDarkMode ? '#1E293B' : '#F8FAFC',
            borderRadius: 10, paddingVertical: 8, paddingHorizontal: 10,
            alignItems: 'center',
          }}>
            <Text style={{ fontSize: 15, fontWeight: '700', color: '#3B82F6' }}>
              {channel.totalJobsScraped.toLocaleString()}
            </Text>
            <Text style={{ fontSize: 10, color: textSecondary, marginTop: 1 }}>Jobs</Text>
          </View>
          <View style={{
            flex: 2, backgroundColor: isDarkMode ? '#1E293B' : '#F8FAFC',
            borderRadius: 10, paddingVertical: 8, paddingHorizontal: 10,
            alignItems: 'center',
          }}>
            <Text style={{ fontSize: 12, fontWeight: '600', color: textPrimary }}>
              {formatDate(channel.lastScraped)}
            </Text>
            <Text style={{ fontSize: 10, color: textSecondary, marginTop: 1 }}>Last Scraped</Text>
          </View>
          <View style={{
            flex: 2, backgroundColor: isDarkMode ? '#1E293B' : '#F8FAFC',
            borderRadius: 10, paddingVertical: 8, paddingHorizontal: 10,
            alignItems: 'center',
          }}>
            <Text style={{ fontSize: 12, fontWeight: '600', color: textPrimary }}>
              {formatDate(channel.createdAt)}
            </Text>
            <Text style={{ fontSize: 10, color: textSecondary, marginTop: 1 }}>Added</Text>
          </View>
        </View>

        {/* Controls row: Active toggle + Scraping toggle + status badges */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 14, paddingBottom: 14,
          gap: 8,
        }}>
          {/* Active toggle */}
          <View style={{
            flex: 1,
            flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
            backgroundColor: isDarkMode ? '#1E293B' : '#F8FAFC',
            borderRadius: 10, paddingHorizontal: 10, paddingVertical: 6,
          }}>
            <Text style={{ fontSize: 11, fontWeight: '600', color: textSecondary }}>Active</Text>
            {isUpdating
              ? <ActivityIndicator size="small" color={colors.success} />
              : <Switch
                  value={channel.isActive}
                  onValueChange={(value) => updateChannel(channel.id, { isActive: value })}
                  trackColor={{ false: isDarkMode ? '#334155' : '#CBD5E1', true: '#10B981' }}
                  thumbColor={channel.isActive ? '#FFFFFF' : '#94A3B8'}
                />
            }
          </View>

          {/* Scraping toggle */}
          <View style={{
            flex: 1,
            flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
            backgroundColor: isDarkMode ? '#1E293B' : '#F8FAFC',
            borderRadius: 10, paddingHorizontal: 10, paddingVertical: 6,
          }}>
            <Text style={{ fontSize: 11, fontWeight: '600', color: textSecondary }}>Scrape</Text>
            {isUpdating
              ? <ActivityIndicator size="small" color={colors.beeYellow} />
              : <Switch
                  value={channel.scrapingEnabled}
                  onValueChange={(value) => updateChannel(channel.id, { scrapingEnabled: value })}
                  trackColor={{ false: isDarkMode ? '#334155' : '#CBD5E1', true: '#F59E0B' }}
                  thumbColor={channel.scrapingEnabled ? '#FFFFFF' : '#94A3B8'}
                />
            }
          </View>
        </View>
      </View>
    );
  };

  // ── Desktop card (grid layout, original design) ────────────────────────────
  const renderDesktopCard = ({ item: channel }: { item: TelegramChannel | null }) => {
    if (!channel) {
      return <View style={[styles.channelCard, styles.placeholderCard]} />;
    }

    const isUpdating = updatingChannels.has(channel.id);

    return (
      <View style={[styles.channelCard, { backgroundColor: colors.white, borderColor: colors.lightGray }]}>
        {/* Delete Button */}
        <TouchableOpacity
          style={[styles.deleteButton, { backgroundColor: colors.danger }]}
          onPress={() => deleteChannel(channel)}
          disabled={isUpdating}
        >
          <Icon name="delete" size={20} color={colors.white} />
        </TouchableOpacity>

        {/* Channel Header */}
        <View style={styles.channelHeader}>
          <View style={styles.channelBasicInfo}>
            <View style={styles.channelImageContainer}>
              {channel.imageUrl ? (
                <Image source={{ uri: channel.imageUrl }} style={styles.channelImage} />
              ) : (
                <View style={styles.defaultImageContainer}>
                  <Text style={styles.defaultImageText}>📱</Text>
                </View>
              )}
            </View>
            <View style={styles.channelInfo}>
              <Text style={[styles.channelUsername, { color: colors.deepNavy }]}>@{channel.username}</Text>
              <Text style={[styles.channelName, { color: colors.charcoal }]}>{channel.name}</Text>
              <Text style={[styles.channelCategory, { color: colors.warmGray }]}>{channel.category}</Text>
            </View>
          </View>
        </View>

        {/* Stats */}
        <View style={[styles.channelStats, { borderTopColor: colors.lightGray, borderBottomColor: colors.lightGray }]}>
          <View style={styles.statItem}>
            <Text style={[styles.statValue, { color: colors.deepNavy }]}>{channel.totalJobsScraped}</Text>
            <Text style={[styles.statLabel, { color: colors.charcoal }]}>Jobs Scraped</Text>
          </View>
          <View style={styles.statItem}>
            <Text style={[styles.statValue, { color: colors.deepNavy }]}>{formatDate(channel.lastScraped)}</Text>
            <Text style={[styles.statLabel, { color: colors.charcoal }]}>Last Scraped</Text>
          </View>
          <View style={styles.statItem}>
            <Text style={[styles.statValue, { color: colors.deepNavy }]}>{formatDate(channel.createdAt)}</Text>
            <Text style={[styles.statLabel, { color: colors.charcoal }]}>Added</Text>
          </View>
        </View>

        {/* Controls */}
        <View style={styles.channelControls}>
          <View style={styles.switchContainer}>
            <Text style={[styles.switchLabel, { color: colors.deepNavy }]}>Active</Text>
            {isUpdating
              ? <ActivityIndicator size="small" color={colors.success} />
              : <Switch
                  value={channel.isActive}
                  onValueChange={(value) => updateChannel(channel.id, { isActive: value })}
                  trackColor={{ false: colors.lightGray, true: colors.success }}
                  thumbColor={channel.isActive ? colors.white : colors.warmGray}
                />
            }
          </View>
          <View style={styles.switchContainer}>
            <Text style={[styles.switchLabel, { color: colors.deepNavy }]}>Scraping</Text>
            {isUpdating
              ? <ActivityIndicator size="small" color={colors.beeYellow} />
              : <Switch
                  value={channel.scrapingEnabled}
                  onValueChange={(value) => updateChannel(channel.id, { scrapingEnabled: value })}
                  trackColor={{ false: colors.lightGray, true: colors.beeYellow }}
                  thumbColor={channel.scrapingEnabled ? colors.white : colors.warmGray}
                />
            }
          </View>
        </View>

        {/* Status Badges */}
        <View style={styles.statusBadges}>
          <View style={[
            styles.statusBadge,
            channel.isActive
              ? { ...styles.activeBadge, backgroundColor: colors.cream, borderColor: colors.beeYellow }
              : { ...styles.inactiveBadge, backgroundColor: colors.lightGray }
          ]}>
            <Text style={[
              styles.statusBadgeText,
              channel.isActive
                ? { ...styles.activeBadgeText, color: colors.success }
                : { ...styles.inactiveBadgeText, color: colors.danger }
            ]}>
              {channel.isActive ? '✅ Active' : '❌ Inactive'}
            </Text>
          </View>
          <View style={[
            styles.statusBadge,
            channel.scrapingEnabled
              ? { ...styles.scrapingBadge, backgroundColor: colors.cream, borderColor: colors.beeYellow }
              : { ...styles.noScrapingBadge, backgroundColor: colors.lightGray }
          ]}>
            <Text style={[
              styles.statusBadgeText,
              channel.scrapingEnabled
                ? { ...styles.scrapingBadgeText, color: colors.deepNavy }
                : { ...styles.noScrapingBadgeText, color: colors.warmGray }
            ]}>
              {channel.scrapingEnabled ? '🔄 Scraping' : '⏸️ Paused'}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: colors.white }]}>
        <ActivityIndicator size="large" color={colors.beeYellow} />
        <Text style={[styles.loadingText, { color: colors.warmGray }]}>Loading channels...</Text>
      </View>
    );
  }

  if (channels.length === 0) {
    return (
      <View style={[styles.emptyContainer, { backgroundColor: colors.white }]}>
        <Text style={styles.emptyIcon}>📱</Text>
        <Text style={[styles.emptyTitle, { color: colors.deepNavy }]}>No Channels Yet</Text>
        <Text style={[styles.emptyDescription, { color: colors.warmGray }]}>
          Add your first Telegram channel to start scraping job posts
        </Text>
      </View>
    );
  }

  const activeChannels = channels.filter(c => c.isActive && c.scrapingEnabled);
  const inactiveChannels = channels.filter(c => !c.isActive || !c.scrapingEnabled);
  const sectionLabelColor = isDarkMode ? '#94A3B8' : colors.deepNavy;

  // ── Mobile layout ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <View style={{ paddingTop: 12, paddingBottom: 4 }}>
        {activeChannels.length > 0 && (
          <View style={{ marginBottom: 8 }}>
            <Text style={{
              fontSize: 11, fontWeight: '700', letterSpacing: 1.1, textTransform: 'uppercase',
              color: '#10B981', paddingHorizontal: 14, paddingBottom: 8,
            }}>
              Active · {activeChannels.length}
            </Text>
            {activeChannels.map((channel) => (
              <View key={channel.id}>
                {renderMobileCard({ item: channel })}
              </View>
            ))}
          </View>
        )}
        {inactiveChannels.length > 0 && (
          <View style={{ marginBottom: 8 }}>
            <Text style={{
              fontSize: 11, fontWeight: '700', letterSpacing: 1.1, textTransform: 'uppercase',
              color: '#64748B', paddingHorizontal: 14, paddingBottom: 8,
            }}>
              Inactive · {inactiveChannels.length}
            </Text>
            {inactiveChannels.map((channel) => (
              <View key={channel.id}>
                {renderMobileCard({ item: channel })}
              </View>
            ))}
          </View>
        )}
      </View>
    );
  }

  // ── Desktop layout (4-column grid) ────────────────────────────────────────
  return (
    <View style={styles.container}>
      {/* Active Channels */}
      {activeChannels.length > 0 && (
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: sectionLabelColor }]}>Active Channels ({activeChannels.length})</Text>
          <FlatList
            data={padArrayForGrid(activeChannels)}
            renderItem={renderDesktopCard}
            keyExtractor={(item, index) => item?.id || `placeholder-${index}`}
            numColumns={4}
            columnWrapperStyle={styles.row}
            scrollEnabled={false}
          />
        </View>
      )}

      {/* Inactive Channels */}
      {inactiveChannels.length > 0 && (
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: sectionLabelColor }]}>Inactive Channels ({inactiveChannels.length})</Text>
          <FlatList
            data={padArrayForGrid(inactiveChannels)}
            renderItem={renderDesktopCard}
            keyExtractor={(item, index) => item?.id || `placeholder-${index}`}
            numColumns={4}
            columnWrapperStyle={styles.row}
            scrollEnabled={false}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    fontSize: 16,
    marginTop: 12,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  emptyDescription: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 12,
  },
  row: {
    justifyContent: 'space-between',
    marginHorizontal: -6, // Negative margin to offset card margins
  },
  channelCard: {
    borderRadius: 16,
    padding: 20,
    margin: 8,
    flex: 1,
    maxWidth: '23%', // Slightly less than 25% to account for margins
    borderWidth: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
  },
  placeholderCard: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    shadowOpacity: 0,
    elevation: 0,
  },
  channelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  channelBasicInfo: {
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  channelImageContainer: {
    marginBottom: 8,
    alignSelf: 'center',
  },
  channelImage: {
    width: 56,
    height: 56,
    borderRadius: 28,
    borderWidth: 2,
  },
  defaultImageContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
  },
  defaultImageText: {
    fontSize: 24,
  },
  channelInfo: {
    flex: 1,
    alignItems: 'center',
  },
  channelUsername: {
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 4,
  },
  channelName: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 4,
    fontWeight: '500',
  },
  channelCategory: {
    fontSize: 11,
    textAlign: 'center',
    textTransform: 'capitalize',
    fontWeight: '500',
  },
  deleteButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    padding: 4,
    borderRadius: 6,
    opacity: 0.8,
  },
  deleteButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  channelStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
    borderBottomWidth: 1,
    marginBottom: 8,
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 13,
    fontWeight: '700',
  },
  statLabel: {
    fontSize: 10,
    marginTop: 2,
    textAlign: 'center',
    fontWeight: '500',
  },
  channelControls: {
    flexDirection: 'column',
    gap: 4,
    marginBottom: 8,
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 4,
  },
  switchLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  statusBadges: {
    flexDirection: 'column',
    gap: 4,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },
  activeBadge: {
    backgroundColor: '#E8F5E8',
  },
  activeBadgeText: {
  },
  inactiveBadge: {
    backgroundColor: '#FFF2F2',
  },
  inactiveBadgeText: {
  },
  scrapingBadge: {
  },
  scrapingBadgeText: {
  },
  noScrapingBadge: {
  },
  noScrapingBadgeText: {
  },
});